'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import DealCard from '@/components/DealCard'

const ORIGIN_LABELS = {
  AMS: 'Amsterdam Schiphol', EIN: 'Eindhoven', RTM: 'Rotterdam The Hague',
  BRU: 'Brussels', CRL: 'Brussels Charleroi',
  LHR: 'London Heathrow', LGW: 'London Gatwick', STN: 'London Stansted',
  LTN: 'London Luton', MAN: 'Manchester', BHX: 'Birmingham',
  EDI: 'Edinburgh', BRS: 'Bristol', NCL: 'Newcastle', GLA: 'Glasgow',
  CDG: 'Paris CDG', ORY: 'Paris Orly',
  FRA: 'Frankfurt', MUC: 'Munich', BER: 'Berlin Brandenburg',
  ZRH: 'Zurich', VIE: 'Vienna', CPH: 'Copenhagen',
  ARN: 'Stockholm Arlanda', OSL: 'Oslo Gardermoen',
  MAD: 'Madrid', BCN: 'Barcelona', LIS: 'Lisbon',
}

function AirportComparison({ deals, destCity }) {
  const best = {}
  for (const d of deals) {
    if (!best[d.origin] || d.price < best[d.origin].price) best[d.origin] = d
  }
  const rows = Object.values(best).sort((a, b) => a.price - b.price)
  if (rows.length < 2) return null
  const lowestPrice = rows[0].price
  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-700 mb-1">Compare from nearby airports</h2>
      <p className="text-xs text-gray-400 mb-3">
        Cheapest available deal to {destCity} per departure airport.
      </p>
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
        {rows.map((deal) => {
          const isBest = deal.price === lowestPrice
          return (
            <div key={deal.origin} className={`flex items-center gap-3 px-4 py-3 ${isBest ? 'bg-green-50' : ''}`}>
              <div className="w-12 shrink-0 text-center">
                <span className="font-mono text-xs font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                  {deal.origin}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">
                  {ORIGIN_LABELS[deal.origin] ?? deal.origin}
                </div>
                {deal.airline && <div className="text-xs text-gray-400">{deal.airline}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className={`text-base font-bold ${isBest ? 'text-green-600' : 'text-gray-700'}`}>
                  €{deal.price}
                </div>
                {isBest && <div className="text-xs text-green-600 font-medium">cheapest ✓</div>}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function LockedDealsCTA({ count, cityName }) {
  return (
    <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-center">
      <p className="text-sm font-semibold text-blue-900 mb-1">
        🔒 {count} more deal{count !== 1 ? 's' : ''} to {cityName} — members only
      </p>
      <p className="text-xs text-blue-700 mb-3">Sign up free to unlock all prices, dates and airports.</p>
      <Link href="/signup" className="inline-block bg-blue-600 text-white text-sm font-bold px-5 py-2 rounded-xl hover:bg-blue-700 transition-colors">
        Get free access →
      </Link>
    </div>
  )
}

export default function DestinationDealsSection({ activeDeals, offSeasonDeals, cityName, tier = "free" }) {
  const [homeAirport, setHomeAirport] = useState(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    try {
      const stored = localStorage.getItem('homeAirport')
      if (stored) setHomeAirport(stored)
    } catch {}
  }, [])

  const isFiltered = !!homeAirport && !showAll

  const visibleActive = isFiltered
    ? activeDeals.filter(d => d.origin === homeAirport)
    : activeDeals

  const visibleOffSeason = isFiltered
    ? offSeasonDeals.filter(d => d.origin === homeAirport)
    : offSeasonDeals

  const shownActive = visibleActive
  const lockedActiveCount = visibleActive.filter(d => d._locked).length
  const showOffSeason = true

  return (
    <>
      {/* Active deals */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-700">
            Active deals to {cityName}
            {visibleActive.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-400">
                {visibleActive.length} deal{visibleActive.length !== 1 ? 's' : ''}
              </span>
            )}
          </h2>
          {homeAirport && (
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full">
              {isFiltered ? `From ${ORIGIN_LABELS[homeAirport] ?? homeAirport}` : 'All airports'}
            </span>
          )}
        </div>
        {shownActive.length > 0 ? (
          <div className="space-y-4">
            {shownActive.map((deal) => (
              <DealCard key={deal.id} deal={deal} hidePhoto locked={!!deal._locked} tier={tier} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
            {isFiltered
              ? `No active deals from ${homeAirport} — try seeing all deals.`
              : 'No active deals right now — check back soon.'}
          </div>
        )}
        {lockedActiveCount > 0 && (
          <div className="mt-4">
            <LockedDealsCTA count={lockedActiveCount} cityName={cityName} />
          </div>
        )}
      </section>

      {/* Off-season deals — members only */}
      {showOffSeason && visibleOffSeason.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-700 mb-1">
            Off-season deals
            <span className="ml-2 text-sm font-normal text-gray-400">
              {visibleOffSeason.length} deal{visibleOffSeason.length !== 1 ? 's' : ''}
            </span>
          </h2>
          <p className="text-xs text-gray-400 mb-3">
            Outside the current season — prices may change when the season opens.
          </p>
          <div className="space-y-4">
            {visibleOffSeason.map((deal) => (
              <DealCard key={deal.id} deal={deal} hidePhoto locked={!!deal._locked} tier={tier} />
            ))}
          </div>
        </section>
      )}

      {/* Airport filter toggle */}
      {homeAirport && (
        <div className="text-sm text-gray-500">
          {isFiltered ? (
            <>
              Showing deals from <strong>{ORIGIN_LABELS[homeAirport] ?? homeAirport}</strong>.{' '}
              <button onClick={() => setShowAll(true)} className="text-blue-600 hover:underline">
                See all deals
              </button>
            </>
          ) : (
            <>
              Showing all airports.{' '}
              <button onClick={() => setShowAll(false)} className="text-blue-600 hover:underline">
                Show only {ORIGIN_LABELS[homeAirport] ?? homeAirport}
              </button>
            </>
          )}
        </div>
      )}

      {/* Airport comparison table */}
      <AirportComparison deals={[...activeDeals, ...offSeasonDeals]} destCity={cityName} />
    </>
  )
}
